import { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import { Button } from './ui/button';
import { Calendar as CalendarPicker } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { useLanguage } from '../contexts/LanguageContext';

export function CalendarStrip({ selectedDate, onDateChange, className = '' }) {
    const { language } = useLanguage();
    const scrollRef = useRef(null);
    const [calendarOpen, setCalendarOpen] = useState(false);

    const generateDays = () => {
        const days = [];
        const today = new Date();
        for (let i = 13; i >= 0; i--) {
            const date = new Date(today);
            date.setDate(today.getDate() - i);
            date.setHours(0, 0, 0, 0);
            days.push(date);
        }
        return days;
    };

    const days = generateDays();

    const formatDay = (date) => {
        const dayNames = language === 'de'
            ? ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa']
            : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        return dayNames[date.getDay()];
    };

    const isToday = (date) => date.toDateString() === new Date().toDateString();
    const isSelected = (date) => selectedDate && date.toDateString() === selectedDate.toDateString();
    const isInStrip = (date) => days.some(d => d.toDateString() === date.toDateString());

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollLeft = scrollRef.current.scrollWidth;
        }
    }, []);

    const handleDateSelect = (date) => {
        const newDate = new Date(date);
        newDate.setHours(12, 0, 0, 0);
        onDateChange(newDate);
        setCalendarOpen(false);
    };

    const scrollLeft = () => scrollRef.current?.scrollBy({ left: -200, behavior: 'smooth' });
    const scrollRight = () => scrollRef.current?.scrollBy({ left: 200, behavior: 'smooth' });

    return (
        <div className={`relative ${className}`}>
            <div className="flex items-center gap-2">
                <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0 hidden sm:flex" onClick={scrollLeft}>
                    <ChevronLeft className="h-4 w-4" />
                </Button>

                <div
                    ref={scrollRef}
                    className="flex gap-1.5 overflow-x-auto scrollbar-hide scroll-smooth flex-1 py-1"
                >
                    {days.map((date, index) => (
                        <motion.button
                            key={index}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => handleDateSelect(date)}
                            className={`
                                flex flex-col items-center justify-center min-w-[56px] h-[72px] rounded-2xl
                                transition-all duration-200 shrink-0 border
                                ${isSelected(date)
                                    ? 'bg-primary text-primary-foreground border-primary shadow-md scale-105'
                                    : isToday(date)
                                        ? 'bg-card text-foreground border-primary/50 font-bold'
                                        : 'bg-card text-muted-foreground border-border hover:border-primary/30 hover:bg-muted/50'
                                }
                            `}
                            data-testid={`date-${date.toISOString().split('T')[0]}`}
                        >
                            <span className="text-[10px] font-medium opacity-70">
                                {formatDay(date)}
                            </span>
                            <span className="text-lg font-bold">
                                {date.getDate()}
                            </span>
                            {isToday(date) && !isSelected(date) && (
                                <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                            )}
                        </motion.button>
                    ))}
                </div>

                <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0 hidden sm:flex" onClick={scrollRight}>
                    <ChevronRight className="h-4 w-4" />
                </Button>

                <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                    <PopoverTrigger asChild>
                        <Button
                            variant="outline"
                            size="icon"
                            className={`h-10 w-10 shrink-0 rounded-xl ${
                                selectedDate && !isInStrip(selectedDate)
                                    ? 'bg-primary text-primary-foreground border-primary'
                                    : ''
                            }`}
                            data-testid="calendar-picker-btn"
                        >
                            <Calendar className="h-4 w-4" />
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="end">
                        <CalendarPicker
                            mode="single"
                            selected={selectedDate}
                            onSelect={handleDateSelect}
                            disabled={(date) => date > new Date()}
                            initialFocus
                        />
                        {selectedDate && !isInStrip(selectedDate) && (
                            <div className="p-2 border-t text-center">
                                <span className="text-sm text-muted-foreground">
                                    {language === 'de' ? 'Ausgewählt: ' : 'Selected: '}
                                    {selectedDate.toLocaleDateString(language === 'de' ? 'de-DE' : 'en-US')}
                                </span>
                            </div>
                        )}
                    </PopoverContent>
                </Popover>
            </div>

            {selectedDate && !isInStrip(selectedDate) && (
                <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-2 text-center"
                >
                    <span className="text-sm text-primary bg-primary/10 px-3 py-1 rounded-full font-medium">
                        {selectedDate.toLocaleDateString(language === 'de' ? 'de-DE' : 'en-US', {
                            weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
                        })}
                    </span>
                </motion.div>
            )}
        </div>
    );
}
